<template>
  <v-container id="content">
    <h1>Résumé de votre diagnostic</h1>

    <!-- Aucune cantine n'est choisie à la place de l'utilisateur quand il en a plusieurs :
         un diagnostic porte sur un établissement précis, en désigner un au hasard fabriquerait
         une restitution qui n'est celle de personne. -->
    <div v-if="canteens.length > 1" class="canteen-picker">
      <label for="swot-canteen">Établissement</label>
      <select id="swot-canteen" v-model="canteenId" @change="load">
        <option v-for="canteen in canteens" :key="canteen.id" :value="canteen.id">{{ canteen.name }}</option>
      </select>
    </div>

    <p v-if="isLoading" role="status">Chargement du diagnostic…</p>

    <v-alert v-else-if="hasError" type="error" text role="alert">
      Le diagnostic n'a pas pu être chargé. Réessayez dans un instant.
    </v-alert>

    <template v-else-if="diagnostic">
      <!-- Frise de maturité : chaque jalon porte son état en toutes lettres. La couleur ne fait
           que redoubler l'information, elle ne la porte jamais seule (RGAA 3.1). -->
      <section aria-labelledby="maturity-title">
        <h2 id="maturity-title">Niveau de maturité</h2>
        <p class="current-level">
          Niveau actuel :
          <strong>{{ maturity.currentLevelName || "aucun niveau atteint" }}</strong>
        </p>

        <v-timeline align="center" dense>
          <v-timeline-item
            v-for="level in maturity.levels"
            :key="level.level"
            :color="level.reached ? 'green darken-2' : 'grey'"
            :icon="level.reached ? 'mdi-check' : 'mdi-circle-outline'"
            :fill-dot="true"
          >
            <div class="milestone">
              <p class="milestone-name">
                Niveau {{ level.level }} — {{ level.name }}
                <span class="milestone-state">({{ level.stateLabel }})</span>
              </p>
              <p v-if="level.reached" class="milestone-detail">{{ level.statement }}</p>
              <p v-else class="milestone-detail">Il reste à : {{ level.criterion }}</p>
            </div>
          </v-timeline-item>
        </v-timeline>

        <!-- Une seule étape suivante, celle du premier palier non franchi. Nommer le critère sans
             donner le geste laissait l'utilisateur chercher l'écran lui-même : le questionnaire
             SWOT, notamment, n'était atteignable qu'en saisissant son URL. -->
        <div class="next-step">
          <template v-if="nextStep">
            <!-- La passation du questionnaire est une page servie par Django, hors du routeur :
                 elle se rejoint par `href`, un `to` ne la résoudrait pas. -->
            <v-btn v-if="nextStep.href" color="primary" :href="nextStep.href">{{ nextStep.label }}</v-btn>
            <v-btn v-else color="primary" :to="nextStep.to">{{ nextStep.label }}</v-btn>
            <p class="next-step-detail">{{ nextStep.detail }}</p>
          </template>
          <p v-else class="next-step-detail" role="status">
            Les trois niveaux sont franchis. Le diagnostic reste modifiable, et une nouvelle campagne de mesure
            actualisera vos chiffres.
          </p>
        </div>
      </section>

      <v-divider class="my-8"></v-divider>

      <!-- Sans diagnostic passé, on le dit. Aucun SWOT de démonstration n'est affiché : un
           exemple ressemble trop à un résultat. -->
      <v-alert v-if="!diagnostic.hasDiagnostic" type="info" text class="mb-0">
        Aucun diagnostic n'a encore été passé pour cet établissement. La matrice s'affichera dès que le questionnaire
        aura été rempli dans son intégralité.
        <div class="mt-3">
          <!-- Ce bouton menait au diagnostic gaspillage, un autre questionnaire : on annonçait la
               matrice SWOT et on ouvrait la saisie des masses. Il mène désormais au questionnaire
               dont cette page restitue les résultats. -->
          <v-btn color="primary" :href="swotSurveyUrl">Répondre au questionnaire de diagnostic</v-btn>
        </div>
      </v-alert>

      <section v-else aria-labelledby="swot-title" class="swot-section">
        <h2 id="swot-title">Analyse SWOT</h2>
        <v-row>
          <v-col v-for="quadrant in quadrants" :key="quadrant" cols="12" md="6">
            <v-card class="swot-card" :class="quadrant.toLowerCase()" outlined>
              <v-card-title>{{ diagnostic.quadrantLabels[quadrant] }}</v-card-title>
              <v-card-text>
                <p v-if="themesIn(quadrant).length === 0" class="empty-quadrant">
                  Aucune thématique dans ce quadrant.
                </p>
                <ul v-else>
                  <!-- Le libellé vient du référentiel, sans jugement incorporé : c'est le
                       placement dans le quadrant qui porte l'appréciation. -->
                  <li v-for="theme in themesIn(quadrant)" :key="theme.key">{{ theme.name }}</li>
                </ul>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <!-- Contrepoint des convives : ce qu'ils disent des thématiques qu'ils vivent en salle. Il
           n'entre pas dans le score du gestionnaire — il l'éclaire. Et il ne s'affiche qu'au-dessus
           du seuil de répondants : sous ce seuil, on annonce l'insuffisance plutôt que de rendre
           une moyenne dont chaque réponse se déduirait. -->
      <section v-if="counterpoint" aria-labelledby="convive-title" class="swot-section">
        <h2 id="convive-title">Ce qu'en disent les convives</h2>

        <p v-if="!counterpoint.sufficient" class="convive-insufficient">{{ counterpoint.message }}</p>

        <template v-else>
          <p class="convive-intro">
            {{ counterpoint.respondentCount }} convives ont répondu en {{ counterpoint.year }}. Les réponses sont
            anonymes : seules des moyennes sont restituées.
          </p>

          <v-card v-for="theme in counterpoint.themes" :key="theme.key" outlined class="mb-4">
            <v-card-title>
              {{ theme.name }}
              <span class="convive-theme-average">— {{ theme.average }} sur 10</span>
            </v-card-title>
            <v-card-text>
              <ul class="convive-questions">
                <li v-for="question in theme.questions" :key="question.label">
                  {{ question.label }}
                  <strong>{{ question.average }} sur 10</strong>
                  <span class="convive-count">({{ question.responseCount }} réponses)</span>
                </li>
              </ul>
            </v-card-text>
          </v-card>
        </template>
      </section>

      <section v-if="weaknessesWithAdvice.length" aria-labelledby="advice-title" class="swot-section">
        <h2 id="advice-title">Pistes sur vos faiblesses</h2>
        <v-card v-for="theme in weaknessesWithAdvice" :key="theme.key" outlined class="mb-4">
          <v-card-title>{{ theme.name }}</v-card-title>
          <v-card-text>
            <div v-for="advice in theme.recommendations" :key="advice.id" class="advice">
              <p class="advice-label">
                {{ advice.label }}
                <span class="advice-nature">— {{ advice.natureLabel }}</span>
              </p>
              <p v-if="advice.description" class="advice-description">{{ advice.description }}</p>

              <v-btn v-if="advice.route" small color="primary" :to="advice.route" class="mt-1">
                Ouvrir l'outil
              </v-btn>

              <template v-if="advice.url">
                <a :href="advice.url" target="_blank" rel="noopener noreferrer">
                  Consulter la ressource (nouvelle fenêtre)
                </a>
                <p class="advice-source">Source : {{ advice.source }} — relevé le {{ advice.surveyedOn }}</p>
                <p class="advice-source">{{ advice.disclaimer }}</p>
              </template>

              <template v-if="advice.canSignalInterest">
                <!-- Pas de bouton désactivé, qui sortirait du parcours clavier : une fois
                     l'intérêt signalé, le bouton cède la place à un texte (RGAA 7.1). -->
                <p v-if="signalled[advice.id]" class="advice-signalled" role="status">
                  Votre intérêt est enregistré. {{ interestLabel(advice) }}
                </p>
                <v-btn v-else small outlined color="primary" class="mt-1" @click="signalInterest(advice)">
                  Signaler mon intérêt
                </v-btn>
              </template>
            </div>
          </v-card-text>
        </v-card>
      </section>
    </template>
  </v-container>
</template>

<script>
import Constants from "@/constants"
import { surveyAnswerUrl } from "@/utils"

// Slug du questionnaire projeté par `seed_swot_diagnostic` (`data/swot_referential.py`).
const SWOT_SURVEY_SLUG = "diagnostic-swot"

export default {
  name: "WasteDiagnosticResumePage",
  data() {
    return {
      diagnostic: null,
      canteenId: null,
      status: Constants.LoadingStatus.IDLE,
      // Intérêts signalés pendant la session, pour ne pas proposer deux fois le même geste.
      signalled: {},
    }
  },
  computed: {
    canteens() {
      return this.$store.state.userCanteenPreviews || []
    },
    isLoading() {
      return this.status === Constants.LoadingStatus.LOADING
    },
    hasError() {
      return this.status === Constants.LoadingStatus.ERROR
    },
    maturity() {
      return (this.diagnostic && this.diagnostic.maturity) || { levels: [], currentLevelName: null }
    },
    swotSurveyUrl() {
      // Page servie par Django, hors du routeur. Le contexte de cantine est porté par la query
      // string : sans lui la réponse s'enregistre sans rattachement, et la restitution ne la
      // retrouve jamais.
      return surveyAnswerUrl({ slug: SWOT_SURVEY_SLUG }, { canteenId: this.canteenId })
    },
    // Le geste qui franchit le premier palier non atteint — un seul à la fois, celui qui vient.
    // Proposer les trois ferait de la frise un menu, alors que les niveaux sont cumulatifs.
    nextStep() {
      if (!this.canteenId) return null
      const level = this.maturity.nextLevel
      if (level === 1) {
        return {
          label: "Passer le niveau 1 : répondre au questionnaire",
          detail: "Trente et une questions, sur le fonctionnement de votre établissement.",
          href: this.swotSurveyUrl,
        }
      }
      if (level === 2) {
        return {
          label: "Passer le niveau 2 : mesurer le gaspillage",
          detail: "Une campagne de mesure sur une période donnée. Vos pesées de service sont reprises si vous en avez.",
          to: { name: "WasteCampaignTunnel", params: { canteenId: String(this.canteenId) } },
        }
      }
      if (level === 3) {
        return {
          label: "Passer le niveau 3 : déclarer une action",
          detail: "Une action sur une thématique placée en Faiblesse par votre diagnostic.",
          to: { name: "CreateAction" },
        }
      }
      return null
    },
    // `null` quand le backend ne rend rien : la section disparaît alors entièrement, plutôt que
    // d'afficher un cadre vide qui se lirait comme « les convives n'ont rien dit de bon ».
    counterpoint() {
      return (this.diagnostic && this.diagnostic.conviveCounterpoint) || null
    },
    quadrants() {
      return Object.keys((this.diagnostic && this.diagnostic.quadrantLabels) || {})
    },
    // Seules les faiblesses effectivement rattachées à quelque chose sont listées. Une faiblesse
    // sans rattachement n'affiche rien plutôt qu'un texte de remplissage.
    weaknessesWithAdvice() {
      const recommendations = (this.diagnostic && this.diagnostic.recommendations) || {}
      return this.themesIn("WEAKNESS")
        .map((theme) => ({ ...theme, recommendations: recommendations[theme.key] || [] }))
        .filter((theme) => theme.recommendations.length > 0)
    },
  },
  methods: {
    themesIn(quadrant) {
      const subscales = (this.diagnostic && this.diagnostic.subscales) || []
      return subscales.filter((subscale) => subscale.quadrant === quadrant)
    },
    interestLabel(advice) {
      const count = advice.interestCount || 0
      return `${count} établissement${count > 1 ? "s" : ""} ont signalé le leur.`
    },
    load() {
      if (!this.canteenId) return Promise.resolve()
      this.status = Constants.LoadingStatus.LOADING
      return this.$store
        .dispatch("fetchSwotDiagnostic", { canteenId: this.canteenId })
        .then((response) => {
          this.diagnostic = response
          this.signalled = {}
          this.status = Constants.LoadingStatus.SUCCESS
        })
        .catch(() => {
          this.status = Constants.LoadingStatus.ERROR
        })
    },
    signalInterest(advice) {
      return this.$store
        .dispatch("signalSwotRecommendationInterest", {
          canteenId: this.canteenId,
          recommendationId: advice.id,
        })
        .then((response) => {
          advice.interestCount = response.interestCount
          this.$set(this.signalled, advice.id, true)
        })
        .catch(() => {
          this.$store.dispatch("notifyServerError")
        })
    },
  },
  mounted() {
    const loggedUser = this.$store.state.loggedUser
    if (loggedUser && loggedUser.canteen) this.canteenId = loggedUser.canteen.id
    else if (this.canteens.length) this.canteenId = this.canteens[0].id
    this.load()
  },
}
</script>

<style scoped>
.canteen-picker {
  margin: 16px 0;
}
.canteen-picker label {
  margin-right: 8px;
  font-weight: 500;
}
.milestone {
  margin-left: 16px;
}
.milestone-name {
  font-weight: 500;
  margin-bottom: 2px;
}
.milestone-state {
  font-weight: 400;
}
.milestone-detail {
  margin-bottom: 0;
  font-size: 0.9rem;
}
.current-level {
  margin-bottom: 16px;
}
.next-step {
  margin-top: 16px;
}
.next-step-detail {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 0.9rem;
}
.swot-section {
  margin-top: 48px;
}
.swot-card {
  min-height: 180px;
}
.empty-quadrant {
  margin-bottom: 0;
  font-style: italic;
}
.advice + .advice {
  margin-top: 20px;
  border-top: 1px solid #e0e0e0;
  padding-top: 20px;
}
.advice-label {
  font-weight: 500;
  margin-bottom: 2px;
}
.advice-nature {
  font-weight: 400;
}
.advice-description,
.advice-source {
  font-size: 0.9rem;
  margin-bottom: 4px;
}
.advice-signalled {
  margin-top: 8px;
  margin-bottom: 0;
}
.convive-intro,
.convive-insufficient {
  margin-bottom: 16px;
}
.convive-theme-average {
  font-weight: 400;
  font-size: 1rem;
  margin-left: 6px;
}
.convive-questions {
  padding-left: 20px;
}
.convive-count {
  font-size: 0.9rem;
}
.strength {
  border-left: 5px solid #43a047;
}
.weakness {
  border-left: 5px solid #e53935;
}
.opportunity {
  border-left: 5px solid #1e88e5;
}
.threat {
  border-left: 5px solid #fbc02d;
}
</style>
